//@flow
import React, {useEffect, useState} from 'react';
import {Typography, makeStyles} from '@material-ui/core';
import FormField from '../../FormField';
import RelatedResourcesModal, {
  getSingularResourceType,
} from '../../../../../../elements/RelatedResources/RelatedResourcesModal';
import CustomButton from '../../../../../../elements/CustomButton/CustomButton';
import {useFormContext} from 'react-hook-form';
import {BLOG_FORM_FIELDS} from '../../../../constants';
import {useCurrentBlogPost} from '../../../../utils/blog-post';
import {getRelatedResourcesService} from '../../../../../related-resources/promises';

import type {BlogFormFieldsRelatedObject} from '../../../../constants';
import type {Resource} from '../../../../../../elements/RelatedResources/RelatedResourcesModal';
import type {RelatedResourceParams} from '../../../../../../entities';
import {
  getRelatedResourcesCriteria,
  getRelatedResourcesParams,
} from '../../../../../related-resources/utils';
import {getImageUrl} from '../../../../../../utils/image_provider';
import IosSwitch from '../../../../../../elements/IosSwitch/IosSwitch';

const useStyles = makeStyles(theme => ({
  formSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1rem',
    borderRadius: '.75rem',
    background: 'white',
  },
  addButton: {
    borderRadius: '.5rem',
  },

  resourceItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    width: '100%',
    padding: '.5rem',
    borderRadius: '.5rem',
  },
  resourceThumbnail: {
    width: '3rem',
    height: '3rem',
    borderRadius: '.5rem',
    overflow: 'hidden',
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  },
  resourceData: {
    flexGrow: 1,
  },
}));

export default function RelatedProducts() {
  const classes = useStyles();

  const {register, setValue, watch} = useFormContext();

  const [modalOpen, setModalOpen] = useState(false);
  const [relatedResources, setRelatedResources] = useState<Resource[]>([]);

  const currentBlog = useCurrentBlogPost();

  const defaultSelected = {
    resources: relatedResources,
    type: relatedResources[0]?.type ?? 'PRODUCTS',
  };

  useEffect(() => {
    register(BLOG_FORM_FIELDS.relatedEntities);
  }, [register]);

  useEffect(() => {
    if (!currentBlog || relatedResources.length > 0) return;
    const relatedEntities = currentBlog.relatedEntities || [];
    if (!relatedEntities.length) return;
    const objectType = relatedEntities[0]?.blogEntityType;
    const ids = relatedEntities.map(object => object.entityIdentifier);

    const params: RelatedResourceParams = getRelatedResourcesParams(
      getRelatedResourcesCriteria(objectType),
      ids
    );

    getRelatedResourcesService(params).then(relatedResource => {
      const newResources: Resource[] = relatedResource?.resources.map(
        resource => ({
          id: resource.id,
          label: resource.name,
          thumbnail: getImageUrl(resource.imageId),
          type: relatedResource.criteria,
        })
      );
      setRelatedResources(newResources);
    });
  }, [currentBlog, relatedResources.length]);

  useEffect(() => {
    const relatedResourcesObject: BlogFormFieldsRelatedObject[] = relatedResources.map(
      resource => ({
        blogEntityType: getSingularResourceType(resource.type),
        entityIdentifier: resource.id,
      })
    );
    setValue(BLOG_FORM_FIELDS.relatedEntities, relatedResourcesObject);
  }, [relatedResources, setValue]);

  const enabledRelatedEntities = watch(BLOG_FORM_FIELDS.enabledRelatedEntities);

  return (
    <section className={classes.formSection}>
      <FormField
        title="9. Recursos Similares"
        subtitle="Configura recursos similares que quieres que tus compradores vean."
        contentRight={
          <IosSwitch
            checked={!!enabledRelatedEntities}
            onChange={e =>
              setValue(
                BLOG_FORM_FIELDS.enabledRelatedEntities,
                e.target.checked
              )
            }
          />
        }
      >
        <RelatedResourcesModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
          }}
          limitResources={5}
          resources={defaultSelected.resources}
          onSubmit={({resources}) => {
            setRelatedResources(resources);
            setModalOpen(false);
          }}
        />
        {relatedResources.map((resource: Resource, index) => (
          <div
            className={classes.resourceItem}
            key={`${resource.id}-${resource.thumbnail || index}`}
          >
            {resource.thumbnail && (
              <picture className={classes.resourceThumbnail}>
                <img src={resource.thumbnail} alt="" />
              </picture>
            )}

            <div className={classes.resourceData}>
              <Typography>{resource.label}</Typography>
            </div>

            <div className={classes.resourceActions}>
              <CustomButton>Eliminar</CustomButton>
            </div>
          </div>
        ))}
        <CustomButton
          className={classes.addButton}
          variant="outlined"
          color="primary"
          onClick={() => {
            setModalOpen(true);
          }}
        >
          Añadir recursos similares
        </CustomButton>
      </FormField>
    </section>
  );
}
